import {Fragment, PropsWithChildren, useEffect, useMemo} from "react";
import {
    Bundle,
    BundleStub,
    ensureLastValidBundleById,
    ensureLastValidBundleForWaste,
    ensureLastValidWasteById,
    KingBasura,
    lastValidBundleForWasteTqo,
    Transfer,
    useBundleById,
    useLastValidBundleForWaste,
    useWasteById,
    useWastesAt,
    Waste,
    WasteStub,
    watchWasteByIdTqo
} from "../rpc/webrpc-client.gen.ts";
import {GateRelay} from "../agent/admitter.tsx";
import {useHomeVenue} from "./homeVenue.tsx";
import {z} from "zod/v4";
import {
    BundleScene,
    BundleTimelineRelay,
    ensureLastSceneForBundle,
    useLastSceneForBundle
} from "../bundle/timeline.tsx";
import {BundleStatus, bundleStatusTqk, ensureBundleStatus, useBundleStatus} from "../bundle/status.tsx";
import {QueryClient, queryOptions, skipToken, useQueries, useQueryClient} from "@tanstack/react-query";
import {InfallibleUseResult, pendingUseResult, successUseResult} from "../util/hookutil.tsx";
import {cleanse, indexesOf, NADA} from "../util/arutil.tsx";
import {
    ensureLastSceneForWaste,
    lastSceneForWasteTqo,
    useLastSceneForWaste,
    WasteScene,
    WasteTimelineRelay
} from "../waste/timeline.tsx";
import {
    ensureWasteStatus,
    unknownWasteStatus,
    useWasteStatus,
    WasteStatus,
    wasteStatusTqk,
    wasteStatusTqo
} from "../waste/waste.tsx";
import {InfallibleUseQueryResult} from "../util/useInfallibleQuery.tsx";
import {useRefreshableInfallibleQuery} from "../util/tqrefresh.tsx";
import {mergeUseQueriesWithInfill} from "../tq/mergeUseQueriesWithInfill.tsx";
import {deadbeefBundle} from "../bundle/bundle.tsx";
import {useMothership} from "../tq/tqx.tsx";

export function useVenuedWasteStubs(args?: { venueId: string }): InfallibleUseResult<WasteStub[]> {
    const tqrv = useWastesAt(args);
    if (tqrv.isPending) {
        return pendingUseResult;
    }
    return successUseResult(cleanse(tqrv.data));
}

export const VenuedWaste = z.object({
    waste: Waste,
    lastScene: z.nullable(WasteScene),
    status: z.nullable(WasteStatus),
    lastBundle: z.nullable(BundleStub),
});
export type VenuedWaste = z.infer<typeof VenuedWaste>;

const venuedWasteVerb = "VenuedWaste";

function venuedWasteTqk(args?: { wasteId: string }) {
    return args ? [venuedWasteVerb, args] as const : [venuedWasteVerb] as const;
}

function ensureVenuedWaste(args: {
    tqc: QueryClient,
    mama: KingBasura,
    args: { wasteId: string }
}): Promise<VenuedWaste> {
    return Promise.all([
        ensureLastValidWasteById(args).then(rpc => rpc.waste),
        ensureLastSceneForWaste(args).then(maybe => maybe ?? null),
        ensureWasteStatus(args),
        ensureLastValidBundleForWaste(args).then(rpc => rpc.bundle ?? null),
    ]).then(([waste, lastScene, status, lastBundle]) => {
        return {waste, lastScene, status, lastBundle};
    });
}

function venuedWasteTqo(args ?: { tqc: QueryClient, mama: KingBasura, args: { wasteId: string } }) {
    return queryOptions({
        queryKey: venuedWasteTqk(args ? args.args : undefined),
        queryFn: args ? () => ensureVenuedWaste(args) : skipToken,
    });
}

export function useVenuedWaste(args?: { wasteId: string }): InfallibleUseQueryResult<VenuedWaste, Error> {
    const tqc = useQueryClient();
    const mama = useMothership();
    return useRefreshableInfallibleQuery(venuedWasteTqo(args ? {tqc, mama, args} : undefined));
}

export function useVenuedWastes(args ?: { venueId: string }): InfallibleUseResult<VenuedWaste[]> {
    const tqc = useQueryClient();
    const mama = useMothership();
    const stubsTqrv = useVenuedWasteStubs(args);
    // const stubs = useMemo(() => stubsTqrv.data ?? [], [stubsTqrv.data]);

    const wastesTqrv = useQueries(stubsTqrv.data
        ? {
            queries: stubsTqrv.data.map(stub => watchWasteByIdTqo(mama, {wasteId: stub.id})),
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});

    const lastScenesTqrv = useQueries(stubsTqrv.data
        ? {
            queries: stubsTqrv.data.map(stub => lastSceneForWasteTqo(mama, {wasteId: stub.id})),
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});

    const statusenTqrv = useQueries(stubsTqrv.data
        ? {
            queries: stubsTqrv.data.map(stub => wasteStatusTqo({tqc, mama, args: {wasteId: stub.id}})),
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});

    const bundlesTqrv = useQueries(stubsTqrv.data
        ? {
            queries: stubsTqrv.data.map(stub => lastValidBundleForWasteTqo(mama, {wasteId: stub.id})),
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});

    const ixen = indexesOf(stubsTqrv.data ?? []);

    const rv = useMemo(() => {
        if (stubsTqrv.isPending || lastScenesTqrv.isPending || statusenTqrv.isPending || bundlesTqrv.isPending) {
            return pendingUseResult;
        }
        const ixws = cleanse(ixen.map(ix => {
            const waste = wastesTqrv.data[ix];
            if (!waste) {
                return undefined;
            }
            const lastScene = lastScenesTqrv.data[ix] ?? null;
            const status = statusenTqrv.data[ix] ?? unknownWasteStatus;
            const lastBundle = bundlesTqrv.data[ix] ?? deadbeefBundle;
            const rv: VenuedWaste = {waste, lastScene, status, lastBundle};
            return rv;
        }));
        return successUseResult(ixws);
    }, [stubsTqrv.isPending, lastScenesTqrv.isPending, lastScenesTqrv.data,
        statusenTqrv.isPending, statusenTqrv.data, bundlesTqrv.isPending,
        bundlesTqrv.data, ixen, wastesTqrv.data]);

    return rv;
}

function VenuedWasteRelay(args: { wasteId: string }) {
    const tqc = useQueryClient();

    const wasteTqrv = useWasteById(args);
    const lastSceneTqrv = useLastSceneForWaste(args);
    const statusTqrv = useWasteStatus(args);
    const bundleTqrv = useLastValidBundleForWaste(args);

    useEffect(() => {
        if (wasteTqrv.isSuccess && !!wasteTqrv.data
            && lastSceneTqrv.isSuccess && statusTqrv.isSuccess && bundleTqrv.isSuccess) {
            const xb: VenuedWaste = {
                waste: wasteTqrv.data,
                lastScene: lastSceneTqrv.data ?? null,
                status: statusTqrv.data,
                lastBundle: bundleTqrv.data ?? null,
            };
            tqc.setQueryData(venuedWasteTqk(args), xb);
        }
    }, [args, wasteTqrv.data, wasteTqrv.isSuccess,
        lastSceneTqrv.data, lastSceneTqrv.isSuccess, tqc,
        statusTqrv.isSuccess, statusTqrv.data, bundleTqrv.isSuccess, bundleTqrv.data]);

    useEffect(() => {
        if (statusTqrv.isSuccess) {
            tqc.setQueryData(wasteStatusTqk({wasteId: args.wasteId}), statusTqrv.data);
        }
    }, [args.wasteId, statusTqrv.data, statusTqrv.isSuccess, tqc]);

    return null;
}

export function useVenuedBundleStubs(args ?: { venueId: string }): InfallibleUseResult<BundleStub[]> {
    const mama = useMothership();
    const wastesTqrv = useWastesAt(args);
    const bundlesTqrv = useQueries(wastesTqrv.isSuccess
        ? {
            queries: wastesTqrv.data.map(wasteStub => lastValidBundleForWasteTqo(mama, {wasteId: wasteStub.id})),
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});
    const uniqueBundles = useMemo(() => {
        const bundlesById = new Map<string, Bundle>();
        if (wastesTqrv.data) {
            for (const bundle of bundlesTqrv.data) {
                if (bundle) {
                    bundlesById.set(bundle.id, bundle);
                }
            }
        }
        return Array.from(bundlesById.values());
    }, [bundlesTqrv.data, wastesTqrv.data]);

    if (wastesTqrv.isPending || bundlesTqrv.isPending) {
        return pendingUseResult;
    }

    return successUseResult(uniqueBundles);
}

export const VenuedBundle = z.object({
    bundle: Bundle,
    lastScene: z.nullable(BundleScene),
    status: BundleStatus,
});

export type VenuedBundle = z.infer<typeof VenuedBundle>;

const venuedBundleVerb = "VenuedBundle";

function venuedBundleTqk(args?: { bundleId: string }) {
    return args ? [venuedBundleVerb, args] as const : [venuedBundleVerb] as const;
}

function ensureVenuedBundle(args: {
    tqc: QueryClient,
    mama: KingBasura,
    args: { bundleId: string }
}): Promise<VenuedBundle> {
    return Promise.all([
        ensureLastValidBundleById(args).then(rpc => rpc.bundle),
        ensureLastSceneForBundle(args).then(maybe => maybe ?? null),
        ensureBundleStatus(args),
    ]).then(([bundle, lastScene, status]) => {
        return {bundle, lastScene: lastScene, status};
    });
}

export function venuedBundleTqo(args ?: { tqc: QueryClient, mama: KingBasura, args: { bundleId: string } }) {
    return queryOptions({
        queryKey: venuedBundleTqk(args ? args.args : undefined),
        queryFn: args ? () => ensureVenuedBundle(args) : skipToken,
    });
}

function VenuedBundleRelay(bidargs: { bundleId: string }) {
    const tqc = useQueryClient();

    const bundleTqrv = useBundleById(bidargs);
    const lastSceneTqrv = useLastSceneForBundle(bidargs);
    const statusTqrv = useBundleStatus(bidargs);

    useEffect(() => {
        if (bundleTqrv.isSuccess && !!bundleTqrv.data
            && lastSceneTqrv.isSuccess
            && statusTqrv.isSuccess) {
            const xb: VenuedBundle = {
                bundle: bundleTqrv.data,
                lastScene: lastSceneTqrv.data ?? null,
                status: statusTqrv.data,
            };
            tqc.setQueryData(venuedBundleTqk(bidargs), xb);
        }
    }, [bidargs, bundleTqrv.data, bundleTqrv.isSuccess,
        lastSceneTqrv.data, lastSceneTqrv.isSuccess,
        statusTqrv.data, statusTqrv.isSuccess, tqc]);

    useEffect(() => {
        if (statusTqrv.isSuccess) {
            tqc.setQueryData(bundleStatusTqk(bidargs), statusTqrv.data);
        }
    }, [bidargs, statusTqrv.data, statusTqrv.isSuccess, tqc]);

    return null;
}

export function isTransferRelevant({transfer, venueId}: { transfer: Transfer, venueId: string }) {
    if (transfer.onloadVenue?.id === venueId && !transfer.onloadEnded) {
        return true;
    }

    if (transfer.offloadVenue?.id === venueId && !transfer.offloadEnded) {
        return true;
    }

    return false;
}

export function VenueRelay({venueId}: { venueId: string }) {
    const bundleStubsTqrv = useVenuedBundleStubs({venueId});
    const wasteStubsTqrv = useVenuedWasteStubs({venueId});

    return <>
        {(bundleStubsTqrv.data
            ? bundleStubsTqrv.data.map(stub => <Fragment key={`venue-${venueId}-bundle-${stub.id}`}>
                <BundleTimelineRelay bundleId={stub.id}/>
                <VenuedBundleRelay bundleId={stub.id}/>
            </Fragment>)
            : null)}
        {(wasteStubsTqrv.data
            ? wasteStubsTqrv.data.map(stub => <Fragment key={`venue-${venueId}-waste-${stub.id}`}>
                <WasteTimelineRelay wasteId={stub.id}/>
                <VenuedWasteRelay wasteId={stub.id}/>
            </Fragment>)
            : null)}
    </>;
}


export function VenueOverviewProvider({children}: PropsWithChildren) {
    const homeVenueTqrv = useHomeVenue();
    const homeVenue = homeVenueTqrv.data;

    return <>
        {homeVenue?.isAdmitting ? <GateRelay venueId={homeVenue.venueId}/> : null}
        {homeVenue?.isInspecting ? <VenueRelay venueId={homeVenue.venueId}/> : null}
        {homeVenue?.isSupervising ? <VenueRelay venueId={homeVenue.venueId}/> : null}
        {children}
    </>
}

